<template>
  <LoadingWrapper :state="status">
    <div class="flex flex-col gap-10">
      <div>
        <div
          v-if="selfAssessments.endedAt === null"
          class="mb-1.5 flex w-full flex-row items-center justify-end gap-1"
        >
          <span class="text-body-small text-on-surface-variant">Aktualisiert sich automatisch</span>
          <span class="bg-tertiary animate-color-pulse h-1.5 w-1.5 rounded-full"></span>
        </div>
        <LessonProgress
          :members="selfAssessments.members"
          :teams="selfAssessments.teams"
          :include-teacher-assessment="selfAssessments.includesTeacherAssessment"
          :lesson-ended="selfAssessments.endedAt !== null"
        />
      </div>
      <div>
        <div class="flex flex-row items-center gap-6 pb-2">
          <h3 class="text-title-large">{{ $t('lumia.lesson.reflection.distribution') }}</h3>
          <PerspectiveLegend
            v-if="selfAssessments.includesTeacherAssessment && showTeacherAssessment"
            :allPerspectives="[SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]"
          />
          <div v-if="selfAssessments.includesTeacherAssessment" class="ml-auto">
            <BaseSwitch
              :value="showTeacherAssessment"
              @valueUpdated="(value) => (showTeacherAssessment = value)"
              :label="$t('lumia.lesson.reflection.showTeacherAssessments')"
            />
          </div>
        </div>
        <IndikatorDistribution
          :indikatoren="indikatoren"
          :scale-items="scaleItems"
          :members="selfAssessments.members"
          :show-teacher="selfAssessments.includesTeacherAssessment && showTeacherAssessment"
        />
      </div>
      <div class="">
        <h3 class="text-title-large pb-1">{{ $t('lumia.sections.reflexionQuestions') }}</h3>
        <div class="grid grid-cols-2 gap-4">
          <ReflexionQuestionCard
            v-for="(question, index) in selfAssessments.reflexionQuestions"
            :key="question.id"
            :index="index"
            :question="question.text"
            :answers="answersForReflexionQuestion(question.id) ?? []"
          />
        </div>
      </div>
      <div>
        <div class="flex flex-row items-center gap-6 pb-2">
          <h3 class="text-title-large">{{ $t('lumia.lesson.reflection.individualStudents') }}</h3>
          <PerspectiveLegend
            v-if="selfAssessments.includesTeacherAssessment && showTeacherAssessment"
            :allPerspectives="[SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]"
          />
          <div v-if="selfAssessments.includesTeacherAssessment" class="ml-auto">
            <BaseSwitch
              :value="showTeacherAssessment"
              @valueUpdated="(value) => (showTeacherAssessment = value)"
              :label="$t('lumia.lesson.reflection.showTeacherAssessments')"
            />
          </div>
        </div>
        <div class="bg-surface-container-low rounded-large w-full min-w-0 p-2">
          <div class="flex w-full min-w-0 flex-col gap-0.5">
            <div class="grid items-center px-2 pt-1 pb-3" :style="{ gridTemplateColumns }">
              <div class="text-label-large text-primary px-2">
                {{ $t('lumia.lesson.reflection.name') }}
              </div>
              <template v-if="showRatingPreview">
                <template v-for="(group, groupIndex) in indikatorGroups" :key="group.id">
                  <div
                    v-for="indikator in group.indikatoren"
                    :key="indikator.id"
                    class="text-title-medium text-primary px-2 py-1.5 text-center"
                  >
                    {{ indikator.number }}
                  </div>
                  <div v-if="groupIndex < indikatorGroups.length - 1" class="flex justify-center">
                    <span class="" />
                  </div>
                </template>
              </template>
              <div />
            </div>

            <div
              v-for="entry in membersWithFeedback"
              :key="entry.member.id"
              class="bg-surface-container-lowest rounded-large w-full min-w-0 overflow-hidden"
              :class="[isExpanded(entry.member.id) && 'my-2']"
            >
              <button
                type="button"
                class="group rounded-large relative grid w-full cursor-pointer items-center overflow-hidden px-2 py-2 text-left"
                :style="{ gridTemplateColumns }"
                :aria-expanded="isExpanded(entry.member.id)"
                @click="toggleExpanded(entry.member.id)"
              >
                <div class="flex min-w-0 items-center gap-2 px-2">
                  <BaseMemberAvatar :member="entry.member" size="32" />
                  <div class="flex flex-col">
                    <span class="text-body-large truncate font-medium">{{
                      memberFullName(entry.member)
                    }}</span>
                    <span class="text-body-small text-on-surface-variant whitespace-nowrap">{{
                      $t('lumia.lesson.reflection.answeredQuestions', {
                        answered: entry.reflexionAnswers.length,
                        total: selfAssessments.reflexionQuestions.length,
                      })
                    }}</span>
                  </div>
                </div>
                <template v-if="showRatingPreview">
                  <template v-for="(group, groupIndex) in indikatorGroups" :key="group.id">
                    <div
                      v-for="indikator in group.indikatoren"
                      :key="indikator.id"
                      class="flex justify-center"
                    >
                      <ScaleValueDots
                        v-if="!isExpanded(entry.member.id)"
                        :items="scaleItems"
                        :values="valueForBar(entry.answers, entry.teacherAnswers, indikator.id)"
                        :allPerspectives="[SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]"
                      />
                    </div>

                    <div v-if="groupIndex < indikatorGroups.length - 1" class="flex justify-center">
                      <span class="" />
                    </div>
                  </template>
                </template>
                <BaseIcon
                  size="24"
                  class="text-on-surface-variant justify-self-center transition-transform"
                  :class="isExpanded(entry.member.id) && 'rotate-180'"
                  >keyboard_arrow_down</BaseIcon
                >
                <BaseStateLayer type="primary" />
              </button>
              <BaseAnimatedExtend
                :is-extended="isExpanded(entry.member.id)"
                class="flex w-full min-w-0"
              >
                <div class="flex flex-col pl-14">
                  <ul class="divide-surface-container-highest -mt-1 flex min-w-0 flex-col divide-y">
                    <li
                      v-for="indikator in indikatoren"
                      :key="indikator.id"
                      class="grid min-w-0 grid-cols-2 items-baseline gap-4 py-3 pr-4"
                    >
                      <div class="col-span-1 flex flex-row items-baseline">
                        <span class="text-title-medium text-primary mr-4 shrink-0 text-center">{{
                          indikator.number
                        }}</span>
                        <span
                          class="text-body-medium text-on-surface-variant wrap-break-words min-w-0 grow text-balance"
                          >{{ memberShortName(entry.member) }} {{ indikator.text }}</span
                        >
                      </div>
                      <div class="flex w-full min-w-0 flex-col items-center gap-4">
                        <PerspectiveValueBarNew
                          class="pt w-full"
                          :values="valueForBar(entry.answers, entry.teacherAnswers, indikator.id)"
                          :allPerspectives="[SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]"
                          :scale="
                            scaleItems.map((item) => ({ value: item.value, label: item.label }))
                          "
                        />
                      </div>
                    </li>
                  </ul>
                  <div
                    v-if="entry.reflexionAnswers.length > 0"
                    class="bg-surface-container-lowest rounded-extra-small last:rounded-b-large"
                  >
                    <h4 class="text-title-small pt-4 pr-4">
                      {{ $t('lumia.sections.reflexionQuestions') }}
                    </h4>
                    <ul
                      class="divide-surface-container-highest -mt-1 flex min-w-0 flex-col divide-y"
                    >
                      <li
                        v-for="(question, index) in selfAssessments.reflexionQuestions"
                        :key="question.id"
                        class="flex min-w-0 flex-col items-baseline gap-1 py-3 pr-4"
                      >
                        <span class="text-label-large text-primary inline-flex">
                          <span class="shrink-0 text-center">{{
                            $t('lumia.lesson.reflection.questionCode', { number: index + 1 })
                          }}</span>
                          ・
                          <span class="">{{ question.text }}</span>
                        </span>
                        <span class="text-body-large text-on-surface-variant italic">{{
                          getReflexionAnswer(entry.reflexionAnswers, question.id)
                        }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </BaseAnimatedExtend>
            </div>
          </div>
          <p
            v-if="membersWithoutFeedbackCount > 0"
            class="text-body-medium text-on-surface-variant px-4 pt-3 pb-1"
          >
            {{
              $t('lumia.lesson.reflection.membersWithoutFeedback', {
                count: membersWithoutFeedbackCount,
              })
            }}
          </p>
        </div>
      </div>
    </div>
  </LoadingWrapper>
</template>

<script lang="ts" setup>
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useSelfAssessments } from '../composables/use-self-assessments'
  import { computed, ref } from 'vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import { memberFullName, memberShortName } from '@/core/models/member/member-name'
  import type { IndikatorAnswer } from '../models/teacher-assessment'
  import type { SelfAssessmentIndikator } from '../models/self-assessments'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import ScaleValueDots from '../components/ScaleValueDots.vue'
  import ReflexionQuestionCard from '../components/ReflexionQuestionCard.vue'
  import IndikatorDistribution from '../components/IndikatorDistribution.vue'
  import LessonProgress from '../components/LessonProgress.vue'
  import BaseSwitch from '@/core/components/base/switch/BaseSwitch.vue'
  import PerspectiveValueBarNew from '@/core/components/analytics/question-list/PerspectiveValueBarNew.vue'
  import { SELF_PERSPECTIVE } from '@/core/models/perspective.ts'
  import { TEACHER_PERSPECTIVE } from '../models/kompetenzprofil.ts'
  import PerspectiveLegend from '@/core/components/analytics/question-list/PerspectiveLegend.vue'

  const { selfAssessments, status } = useSelfAssessments()

  interface NumberedIndikator extends SelfAssessmentIndikator {
    readonly number: number
  }

  const indikatorGroups = computed(() => {
    let number = 1
    return (selfAssessments.value?.kompetenzen ?? [])
      .map((kompetenz) => ({
        id: kompetenz.id,
        indikatoren: [
          ...(kompetenz.indikatoren ?? []),
          ...kompetenz.kompetenzstufen.flatMap((stufe) => stufe.indikatoren),
        ].map(
          (indikator): NumberedIndikator => ({
            ...indikator,
            number: number++,
          }),
        ),
      }))
      .filter((group) => group.indikatoren.length > 0)
  })

  const indikatoren = computed(() => indikatorGroups.value.flatMap((group) => group.indikatoren))

  const showRatingPreview = computed(() => indikatoren.value.length <= 4)

  const membersWithFeedback = computed(
    () =>
      selfAssessments.value?.members.filter(
        (member) => member.answers.length > 0 || member.teacherAnswers.length > 0,
      ) ?? [],
  )

  const membersWithoutFeedbackCount = computed(() => {
    const members = selfAssessments.value?.members ?? []
    return members.length - membersWithFeedback.value.length
  })

  const gridTemplateColumns = computed(() => {
    const columns = ['minmax(12rem, 1fr)']
    if (showRatingPreview.value) {
      indikatorGroups.value.forEach((group, index) => {
        columns.push(`repeat(${group.indikatoren.length}, minmax(4.25rem, 1fr))`)
        if (index < indikatorGroups.value.length - 1) {
          columns.push('0.75rem')
        }
      })
    }
    columns.push('2.5rem')
    return columns.join(' ')
  })

  const scaleItems = computed(() => selfAssessments.value?.selfScale?.items ?? [])

  const answerValue = (answers: readonly IndikatorAnswer[], indikatorId: string) =>
    answers.find((answer) => answer.indikatorId === indikatorId)?.value ?? null

  const expandedMemberIds = ref<Readonly<Record<string, boolean>>>({})

  const isExpanded = (memberId: string) => expandedMemberIds.value[memberId] === true

  const toggleExpanded = (memberId: string) => {
    expandedMemberIds.value = {
      ...expandedMemberIds.value,
      [memberId]: !expandedMemberIds.value[memberId],
    }
  }
  const getReflexionAnswer = (
    reflexionAnswers: readonly { readonly reflexionQuestionId: string; readonly text: string }[],
    questionId: string,
  ) => reflexionAnswers.find((answer) => answer.reflexionQuestionId === questionId)?.text ?? ''

  const answersForReflexionQuestion = (questionId: string) =>
    selfAssessments.value?.members.flatMap((member) => {
      const answer = member.reflexionAnswers.find(
        (answer) => answer.reflexionQuestionId === questionId,
      )
      if (answer == null) return []

      return {
        member: member.member,
        answer: answer.text,
      }
    })

  const showTeacherAssessment = ref(false)

  const valueForBar = (
    answers: readonly IndikatorAnswer[],
    teacherAnswers: readonly IndikatorAnswer[],
    indikatorId: string,
  ) => {
    const result = []

    if (answers.length > 0) {
      result.push({
        perspective: SELF_PERSPECTIVE,
        average: answerValue(answers, indikatorId),
        numberOfFeedbacks: 1,
      })
    }

    if (showTeacherAssessment.value) {
      result.push({
        perspective: TEACHER_PERSPECTIVE,
        average: answerValue(teacherAnswers, indikatorId),
        numberOfFeedbacks: 1,
      })
    }

    return result
  }
</script>
